import { useEffect, useState } from 'react';
import { imageRecognition } from '~libs';
import {
  CallbackCamera,
  ErrorPictureResponse,
  SuccessPictureResponse,
  SupportPictureResponse,
  DocumentCameraType,
  DocumentCameraTypes,
  UnicoConfig,
} from 'unico-webframe';
import { useNavigate } from 'react-router-dom';
import { CameraPlaceholder, PhotoConfirmation } from '~components';
import { useRequest } from 'ahooks';
import { Representative, Spouse } from '~services';
import { Response, ParamsFile } from '~types/getUserData';
import { useTransshipment } from '~contexts/TranshipmentContext';
import { useLayout } from '~hooks';
import * as amplitude from '@amplitude/analytics-browser';
import { unicoConfig } from '~libs/imageRecognition';

function CnhCamera({ onClickCloseCamera }: { onClickCloseCamera: () => void }) {
  const transshipment = useTransshipment();
  const [showPhotoConfirmation, setShowPhotoConfirmation] = useState(false);
  const [base64Image, setBase64Image] = useState('');
  const navigate = useNavigate();

  const request =
    transshipment.type === 'spouse'
      ? Spouse.addDocument
      : Representative.addDocument;

  const {
    runAsync: addDocument,
    error,
    loading: isLoadingAddDocument,
  } = useRequest<Response, ParamsFile>(request, {
    onSuccess: successAddDocument,
    manual: true,
  });

  async function successAddDocument() {
    if (transshipment.type === 'representative') {
      amplitude.track({
        event_type: 'documentation_id_sent',
        event_properties: {
          CPF_representatives: transshipment.data.cpf,
        },
      });
    }
    transshipment.nextStep();
  }

  useEffect(() => {
    if (error) throw error;
  }, [error]);

  const { setLayout } = useLayout();

  const callbacks: CallbackCamera = {
    on: {
      success: function (response: SuccessPictureResponse) {
        setBase64Image(response.base64);
        setShowPhotoConfirmation(true);
      },
      error: function (error: ErrorPictureResponse) {},
      support: function (error: SupportPictureResponse) {},
    },
  };

  async function handleSuccess() {
    await addDocument(transshipment.id, {
      file: base64Image,
      type: 'cnh',
    });

    setLayout({ disabledLayout: false, header: {} });
  }

  const openDocumentCamera = async (
    jsonPath: UnicoConfig,
    cameraType: DocumentCameraType
  ) => {
    try {
      const { open } = await imageRecognition.prepareDocumentCamera(
        jsonPath,
        cameraType
      );

      open(callbacks);
    } catch (error) {
      navigate('/web/internet');
    }
  };

  const takeAnotherPhoto = () => {
    setShowPhotoConfirmation(false);
    openDocumentCamera(unicoConfig, DocumentCameraTypes.CNH);
  };

  useEffect(() => {
    setLayout({ disabledLayout: true, header: {} });
    openDocumentCamera(unicoConfig, DocumentCameraTypes.CNH);
  }, []);

  return showPhotoConfirmation ? (
    <PhotoConfirmation
      base64Image={base64Image}
      onClickIsGood={handleSuccess}
      onClickTakeAnother={takeAnotherPhoto}
      isLoadingConfirmation={isLoadingAddDocument}
    />
  ) : (
    <CameraPlaceholder onClickClose={onClickCloseCamera} />
  );
}

export default CnhCamera;
